import { useApiClient } from '_core/api/context';
import { fetchAllPages } from '_core/api/fetchAllPages';
import { Breadcrumbs, BreadcrumbsItem } from '_core/breadcrumbs';
import { CenteredSpinner } from '_core/feedback/centeredSpinner';
import { GenericErrorMessage } from '_core/feedback/genericErrorMessage';
import { submissionErrorsFromApiError } from '_core/final-form/submissionErrorsFromApiError';
import { selectMe } from '_core/me/me';
import { parseJsonWithFallback } from '_core/parseJsonWithFallback';
import { FormattedTitle } from '_core/react-head/formattedTitle';
import { useFormNavigationLock } from '_core/router5/navigationLock';
import { useAsyncData } from '_core/useAsyncData';
import { useErpSelector } from '_erp/redux';
import { fetchUsers } from 'accounts/api';
import { Row, VGrid } from 'layout/contentLayout';
import { IPartnerSerialized } from 'partners/api';
import * as React from 'react';
import { useMemo } from 'react';
import { useRouteNode } from 'react-router5';
import invariant from 'tiny-invariant';

import { IPartnerFormValues, PartnersForm } from './form';

export default function PartnersCreateRoute() {
  const { route, router } = useRouteNode('partners.create');
  const navigationLock = useFormNavigationLock('partners.create');
  const api = useApiClient();

  const currentUserProfile = useErpSelector(selectMe);
  invariant(currentUserProfile);

  const { data, isFetching } = useAsyncData(
    [api, route.params.id],
    async () => {
      const workersResponse = await fetchAllPages(page =>
        fetchUsers(api, { isEmployee: true, page })
      );

      return {
        workers: workersResponse.results,
      };
    }
  );

  const listParams = useMemo(
    () => parseJsonWithFallback(route.params.listParams, {}),
    [route.params.listParams]
  );

  if (!data) {
    return isFetching ? <CenteredSpinner /> : <GenericErrorMessage />;
  }

  const persist = (values: IPartnerFormValues) =>
    api.post<IPartnerSerialized>('/partners', {
      ...values,
      manager: Number(values.manager),
      shortName: values.shortName.toUpperCase(),
    });

  const title = 'Новый контрагент';

  function handleSavingError(err: unknown) {
    return submissionErrorsFromApiError(
      err,
      'Не создать контрагента: Непредвиденная ошибка'
    );
  }

  return (
    <>
      <FormattedTitle>{title}</FormattedTitle>

      <VGrid>
        <Row>
          <Breadcrumbs>
            <BreadcrumbsItem
              label="Контрагенты"
              params={listParams}
              to="partners"
            />

            <BreadcrumbsItem label={title} />
          </Breadcrumbs>
        </Row>

        <Row>
          <PartnersForm
            initialValues={{
              actualAddress: '',
              bankName: '',
              bik: '',
              checkingAccount: '',
              contacts: [],
              correspondentAccount: '',
              email: '',
              fullName: '',
              inn: '',
              isClient: false,
              isSupplier: false,
              kpp: '',
              legalAddress: '',
              manager: String(currentUserProfile.id),
              ogrn: '',
              okato: '',
              okpo: '',
              oktmo: '',
              paymentData: '',
              postAddress: '',
              shortName: '',
              type: '',
            }}
            navigationLock={navigationLock}
            workers={data.workers}
            onSave={async values => {
              try {
                await persist(values);
                navigationLock.unlock();
                router.navigate('partners', listParams);
                return undefined;
              } catch (err) {
                return handleSavingError(err);
              }
            }}
            onSaveAndContinue={async values => {
              try {
                const createdPartner = await persist(values);
                navigationLock.unlock();
                router.navigate('partners.edit', {
                  id: createdPartner.id,
                  listParams: route.params.listParams,
                });
                return undefined;
              } catch (err) {
                return handleSavingError(err);
              }
            }}
          />
        </Row>
      </VGrid>
    </>
  );
}
